import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';

import { CustomValidators } from './../../../../../core/utils/CustomValidators';
import { Application } from './../../../../../core/interfaces/system-manager';
import { ApplicationType, PBXTypes } from './../../../../../core/constants/constants';
import { TenantSipTrunkProtocol, TenantSipTrunkType, TenantSipTrunk } from './../../../../../core/interfaces/tenants';

export interface SIPTrunkConfigForm {
  pbxType: FormControl<string>;
  mbg: FormControl<string>;
};
export interface SIPTrunkDataForm {
  pbx: FormControl<string>;
  tlsPort: FormControl<string>;
  name: FormControl<string>;
  ip: FormControl<string>;
};
export interface SIPTrunkData {
  trunkId: string;
  trunkName: string;
  pbx: string;
  port: string;
  ip: string;
  selected: boolean;
};

export enum sipTrunksConfigState {
  loading = 'loading',
  view = 'view',
  actionInProgress = 'actionInProgress'
}

@Component({
  selector: 'app-sip-trunks-config',
  templateUrl: './sip-trunks-config.component.html',
  styleUrls: ['./sip-trunks-config.component.scss']
})
export class SipTrunksConfigComponent implements OnInit {
  showFormError: boolean = false;
  errMsg: string = '';
  pbxTypes: string[] = []; //constants -> PBXTypes
  mbgs: Application[] = [];
  pbxs: Application[] = [];

  setupComplete: boolean = false;
  
  configForm: FormGroup<SIPTrunkConfigForm>;
  mbgRequired: boolean = false;
  isConnectivityEstablished: boolean = false;

  sipTrunkForm: FormGroup<SIPTrunkDataForm>;
  sipTrunks: TenantSipTrunk[] = [];
  sipTrunkData: SIPTrunkData[] = [];
  countSelected = 0;

  searchInProgress: boolean = false;
  dataSource: MatTableDataSource<SIPTrunkData> = new MatTableDataSource([]);
  sort!: MatSort;
  @ViewChild(MatSort) set matSort(ms: MatSort) {
    this.sort = ms;
    this.dataSource.sort = this.sort;
  };

  displayedColumns: string[] = ['select', 'trunkName', 'port', 'pbx', 'ip'];
  showAddTrunkSection: boolean = true;
  editing: boolean = false;
  state: sipTrunksConfigState = sipTrunksConfigState.loading;
  tenantFound: boolean = false;

  constructor(@Inject(MAT_DIALOG_DATA) public data: any,
    public dialogRef: MatDialogRef<SipTrunksConfigComponent>,
    private formBuilder: FormBuilder,
    private domSanitizer: DomSanitizer,
    private matIconRegistry: MatIconRegistry,
    private translateService: TranslateService) {
    this.matIconRegistry.addSvgIcon(
      'search_app',
      this.domSanitizer.bypassSecurityTrustResourceUrl("../assets/images/search-big.svg")
    );
  }
  
  async ngOnInit() {
    this.configForm = this.formBuilder.group({
      pbxType: this.formBuilder.control(''),
      mbg: this.formBuilder.control('')
    });

    this.sipTrunkForm = this.formBuilder.group({
      pbx: this.formBuilder.control('', [Validators.required]),
      tlsPort: this.formBuilder.control('', [Validators.required, CustomValidators.validatePortNumber]),
      name: this.formBuilder.control('', [Validators.required]),
      ip: this.formBuilder.control('', [Validators.required, CustomValidators.validateIpAddressOrFQDN])
    });
    
    await this.setConnectivitySettings();
    await this.readConnectivitySettings();

    this.checkConnectivitySettings();
    
    await this.readSIPTrunks();
  }

  async setConnectivitySettings() {
    try {
      let applications: Application[] = await this.data.product.service.getSIPApplications(this.data.account);

      if (applications?.length) {
        applications.sort((application1: Application, application2: Application) => application1?.applicationName?.localeCompare(application2?.applicationName));
        const mivo5000PBXs = applications.filter(application => application.applicationType === ApplicationType.MIVOICE_5000) ?? [];
        if (mivo5000PBXs?.length) {
          this.pbxTypes.push(PBXTypes.mivo5000);
          this.pbxs = this.pbxs.concat(mivo5000PBXs);
        }

        const mivbPBXs = applications.filter(application => application.applicationType === ApplicationType.MIVOICE_BUSINESS) ?? [];
        if (mivbPBXs?.length) {
          this.pbxTypes.push(PBXTypes.mivb);
          this.pbxs = this.pbxs.concat(mivbPBXs);
          this.mbgs = applications.filter(application => application.applicationType === ApplicationType.MITEL_BORDER_GATEWAY) ?? [];
        }

        if (this.pbxTypes.length == 1) {
          const selectedPBXType = this.pbxTypes[0];
          this.configForm.controls['pbxType'].setValue(this.pbxTypes[0]);
          this.configForm.controls['pbxType'].disable();
          this.mbgRequired = selectedPBXType === PBXTypes.mivb;
          
          if (this.mbgRequired && this.mbgs.length == 1) {
            this.configForm.controls['mbg'].setValue(this.mbgs[0].applicationId);
            this.configForm.controls['mbg'].disable();
          }
        }
      } else {
        this.errMsg = 'integrations.sip-trunks.config-dialog.missing-connectivity-settings';
        this.showFormError = true;
      }
    } catch (error) {
      this.errMsg = 'integrations.sip-trunks.config-dialog.error-establishing-connectivity-settings';
      this.showFormError = true;
    } finally {
      this.state = sipTrunksConfigState.view;
    }
  }
  
  async readConnectivitySettings() {
    if (!this.showFormError) {
      this.isConnectivityEstablished = this.data.product.setupComplete;
      if (this.isConnectivityEstablished) {
        let tags = await this.data.product.service.getIntegrationTags(this.data.account);

        if (tags?.pbxType) {
          //read pbxType from the tags
          const selectedPBXType = PBXTypes[tags.pbxType];
          this.configForm.controls['pbxType'].setValue(selectedPBXType);
          this.configForm.controls['pbxType'].disable();
          this.mbgRequired = selectedPBXType === PBXTypes.mivb;

          if (this.mbgRequired) {
            this.configForm.controls['mbg'].setValue(tags.mbgApplicationId);
            this.configForm.controls['mbg'].disable();
          }
        } else {
          this.errMsg = 'integrations.sip-trunks.config-dialog.missing-account-tags-pbx-type';
          this.showFormError = true;
        }
      }
    }
  }

  checkConnectivitySettings() {
    if (!this.showFormError) {
      if (this.mbgRequired && !this.mbgs?.length) {
        this.errMsg = 'integrations.sip-trunks.config-dialog.missing-connectivity-settings-mbg';
        this.showFormError = true;
      }
    }
  }

  pbxTypeChanged(value: string) {
    this.mbgRequired = value === PBXTypes.mivb;
    this.checkConnectivitySettings();
  }

  pbxChanged(value: string) {
    let selectedPBX = this.pbxs.find(application => application.applicationId == value);
    this.sipTrunkForm.controls['ip'].setValue(selectedPBX?.ipAddress);
  }

  async readSIPTrunks() {
    try {
      this.state = sipTrunksConfigState.actionInProgress;
      if (this.isConnectivityEstablished) {
        this.sipTrunks = await this.data.product.service.getSIPTrunks(this.data.account);
        this.initDataSource();
        
        //hide add section after adding first SIP trunk
        this.showAddTrunkSection = !this.sipTrunks?.length;
        this.tenantFound = true;
      }
    } catch (error) {
      this.showFormError = true;
      if (error == 'Missing tenant for account') {
        this.errMsg = 'integrations.unify-phone.no-tenant-message';
        this.tenantFound = false;
      } else {
        this.errMsg = 'integrations.sip-trunks.config-dialog.error-getting-sip-trunks';
        this.tenantFound = true;
      }
    } finally {
      this.state = sipTrunksConfigState.view;
    }
  } 

  async addSIPTrunk() {
    try {
      this.state = sipTrunksConfigState.actionInProgress;
      this.clearError();
      this.disableSipTrunkForm();
      let selectedPBX = this.pbxs.find(application => application.applicationId == this.sipTrunkForm.controls['pbx']?.value);
      const sipTrunkData = {
        trunkName: this.sipTrunkForm.controls['name']?.value,
        type: TenantSipTrunkType.MiV5000_TRUNK_TYPE,
        ip: this.sipTrunkForm.controls['ip']?.value ?? selectedPBX.ipAddress,
        port: this.sipTrunkForm.controls['tlsPort']?.value,
        protocol: TenantSipTrunkProtocol.TLS
      };

      const result = await this.data.product.service.addSIPTrunk(this.data.account, sipTrunkData);
      this.sipTrunks.push(result);

      this.initDataSource();

      this.clearSipTrunkForm();

      //hide add section after adding first SIP trunk
      this.showAddTrunkSection = !this.sipTrunks?.length;
    } catch (error) {
      this.errMsg = 'integrations.sip-trunks.config-dialog.error-adding-sip-trunk';
      this.showFormError = true;
    } finally {
      this.enableSipTrunkForm();
      this.state = sipTrunksConfigState.view;
    }
  }

  disableSipTrunkForm() {
    for (var name in this.sipTrunkForm.controls) {
      this.sipTrunkForm.controls[name].disable();
    }
  }

  clearSipTrunkForm() {
    for (var name in this.sipTrunkForm.controls) {
      this.sipTrunkForm.controls[name].setValue('');
      this.sipTrunkForm.markAsUntouched();
    }
  }

  enableSipTrunkForm() {
    for (var name in this.sipTrunkForm.controls) {
      this.sipTrunkForm.controls[name].enable();
    }
  }

  editSIPTrunk() {
    const selectedTrunkIds = this.sipTrunkData.filter(trunk => trunk.selected).map(trunk => trunk.trunkId);
    const trunks = this.sipTrunks.filter(trunk => selectedTrunkIds.find(trunkId => trunk.trunkId === trunkId));

    if(trunks.length == 1) {
      this.editing = true;
      const selectedSipTrunk = trunks[0];
      const selectedPBX = this.pbxs.find(pbx => pbx.ipAddress == selectedSipTrunk.ip);
      this.sipTrunkForm.controls['pbx'].patchValue(selectedPBX?.applicationId ?? this.pbxs[0].applicationId); // populate the first by default ??
      this.sipTrunkForm.controls['name'].patchValue(trunks[0].trunkName);
      this.sipTrunkForm.controls['tlsPort'].patchValue(trunks[0].port);
      this.sipTrunkForm.controls['ip'].patchValue(trunks[0].ip);
      this.showAddTrunkSection = true;
    } // unlikely
  }

  doneEditSIPTrunk () {
    this.showAddTrunkSection = false;
    this.clearSipTrunkForm();
    this.clearError();
    this.sipTrunkData.filter(trunk => trunk.selected).map(trunk => this.selectionChange(trunk));
    this.editing = false;
    this.state = sipTrunksConfigState.view;
  }

  async updateSIPTrunk () {
    try {
      this.state = sipTrunksConfigState.actionInProgress;
      this.disableSipTrunkForm();
      const selectedTrunkIds = this.sipTrunkData.filter(trunk => trunk.selected).map(trunk => trunk.trunkId);
      const trunks = this.sipTrunks.filter(trunk => selectedTrunkIds.find(trunkId => trunk.trunkId === trunkId));

      if (trunks?.length) {
        // update first sip trunk only
        let selectedPBX = this.pbxs.find(application => application.applicationId == this.sipTrunkForm.controls['pbx']?.value);
        const sipTrunkData = {
          trunkId: trunks[0].trunkId,
          trunkName: this.sipTrunkForm.controls['name']?.value,
          type: TenantSipTrunkType.MiV5000_TRUNK_TYPE,
          ip: this.sipTrunkForm.controls['ip']?.value ?? selectedPBX.ipAddress,
          port: this.sipTrunkForm.controls['tlsPort']?.value,
          protocol: TenantSipTrunkProtocol.TLS
        };
  
        const result = await this.data.product.service.updateSIPTrunk(this.data.account, sipTrunkData);
        const index = this.sipTrunks.findIndex(trunk => trunk.trunkId === result.trunkId);

        if(index >= 0) {
          this.sipTrunks[index].trunkName = result.trunkName;
          this.sipTrunks[index].ip = result.ip;
          this.sipTrunks[index].port = result.port;
        }
  
        this.initDataSource();
  
        this.doneEditSIPTrunk();
      }      
    } catch (error) {
      this.errMsg = 'integrations.sip-trunks.config-dialog.error-updating-sip-trunk';
      this.showFormError = true;
    } finally {
      this.enableSipTrunkForm();
      this.state = sipTrunksConfigState.view;
    }
  }

  async deleteSIPTrunks() {
    try {
      this.clearError();
      const selectedTrunkIds = this.sipTrunkData.filter(trunk => trunk.selected).map(trunk => trunk.trunkId);
      const trunks = this.sipTrunks.filter(trunk => selectedTrunkIds.find(trunkId => trunk.trunkId === trunkId));

      if (trunks?.length) {
        let deletedSipTrunkIds = await this.data.product.service.deleteSIPTrunks(this.data.account, trunks);
        if (deletedSipTrunkIds?.length) {
          // use splice to keep reference to array
          for (let i = this.sipTrunks.length - 1; i >= 0; i--) {
            if (deletedSipTrunkIds.find(trunkId => this.sipTrunks[i].trunkId === trunkId)) {
              this.sipTrunks.splice(i, 1);
            }
          }

          this.dataSource.filter = '';
          this.initDataSource();

          //show add section if no sip trunk
          this.showAddTrunkSection = !this.sipTrunks?.length;
        }
      }
    } catch (error) {
      this.errMsg = 'integrations.sip-trunks.config-dialog.error-deleting-sip-trunks';
      this.showFormError = true;
    }
  }

  clearError() {
    this.errMsg = '';
    this.showFormError = false;
  }


  //#region Trunk list

  //#region Select
  selectableTrunks(): boolean {
    return this.sipTrunkData.length > 0;
  }

  selectionAllChange(): void {
    let selectAll = this.allSelected() ? false : true;
    this.sipTrunkData.map(trunk => {
      trunk.selected = selectAll;
    });
    this.countSelected = selectAll ? this.sipTrunkData.filter(trunk => trunk.selected).length : 0;
  }

  selectionChange(trunk: SIPTrunkData): void {
    trunk.selected = !trunk.selected;
    this.countSelected += trunk.selected ? 1 : -1;
  }

  allSelected(): boolean {
    return Boolean(this.sipTrunkData?.length && this.countSelected > 0 && this.countSelected === this.sipTrunkData.length);
  }

  someSelected(): boolean {
    if (this.dataSource?.data?.length === 0) {
      return false;
    }
    return this.countSelected > 0 && this.countSelected < this.sipTrunkData.length;
  }

  updateCountSelection(): void {
    this.countSelected = this.sipTrunkData.reduce((acc, cur) => cur.selected ? ++acc : acc, 0);
  }
  //#endregion
  
  initDataSource(): void {
    this.sipTrunkData = this.processData(this.sipTrunks).filter(trunk => this.customSearch(trunk));
    this.dataSource = new MatTableDataSource(this.sipTrunkData);
    this.dataSource.sort = this.sort;
    this.updateCountSelection();
  }

  processData(sipTrunks: TenantSipTrunk[]): SIPTrunkData[] {
    if (sipTrunks.length > 0) {
      return sipTrunks
        .map(sipTrunk => {
          return { ...sipTrunk, pbx: this.pbxs.find(pbx => pbx.ipAddress == sipTrunk.ip)?.applicationName ?? sipTrunk.ip, selected: false } as any;
        })
        .sort((sipTrunk1, sipTrunk2) => sipTrunk1?.trunkName.localeCompare(sipTrunk2?.trunkName));
    } else {
      return [];
    }
  }

  customSearch(sipTrunk: SIPTrunkData): boolean {
    let filter = this.dataSource?.filter.trim().toLowerCase() ?? '';
    return !filter ? true :
      sipTrunk?.trunkName.trim().toLowerCase().indexOf(filter) !== -1 ||
      sipTrunk?.pbx.trim().toLowerCase().indexOf(filter) !== -1 ||
      sipTrunk?.ip.toLowerCase().indexOf(filter) !== -1 ||
      sipTrunk?.port.indexOf(filter) !== -1||
      sipTrunk?.ip.toLowerCase().indexOf(filter) !== -1;
  }

  onSearch(event: string): void {
    this.dataSource.filter = event?.trim().toLowerCase() ?? '';
    this.initDataSource();
  }
  //#endregion

  async onSave() {
    this.showFormError = false;
    this.errMsg = '';

    const sipTrunkConfig = {
      pbxType: Object.keys(PBXTypes).find(key => PBXTypes[key] === this.configForm.controls['pbxType'].value),
      mbgApplicationId: this.configForm.controls['mbg'].value
    };

    try {
      let result = await this.data.product.service.saveConfigurationData(this.data.account, sipTrunkConfig);
      this.isConnectivityEstablished = result;
      if (this.isConnectivityEstablished) {
        this.configForm.controls['pbxType'].disable();
        if (this.mbgRequired) {
          this.configForm.controls['mbg'].disable();
        }
        await this.readSIPTrunks();
      }
    } catch (error) {
      this.errMsg = 'integrations.sip-trunks.config-dialog.error-configuring-proxy';
      this.showFormError = true;
      console.log(error);
    }
  }

  onCancel() {
    this.dialogRef.close({ setupComplete: this.isConnectivityEstablished });
  }
}
